/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import type { WireTemplateQueryDto } from '@treasury/domain/wires';
import { WireAccount, WiresService, WireTemplate } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../../components/jhd-header-bar';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../../partials/wires-container-sidebar';
import '../../partials/wires-container-tabs';
import { WireTemplatesParameters } from '../../types/wire-templates-parameters.type';
import { ValidWireDateTime } from '../../types/wire-valid-datetime.type';
import type { WireAction } from '../../types/wire-workflow-parameters.type';
import '../partials/wire-template-detail-sheet';
import '../partials/wire-templates-filters-sheet';
import '../partials/wire-templates-table';

export const tagName = 'wire-templates-container';
@customElement(tagName)
export class WireTemplatesContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private wiresService: WiresService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private config: ConfigurationService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @state()
    private wireTemplates: WireTemplate[] = [];

    @state()
    private selectedWireTemplate: WireTemplate = new WireTemplate();

    @state()
    private templateFilters?: WireTemplateQueryDto;

    @state()
    private templateDetailsSheetOpen = false;

    @state()
    private transferAccounts: WireAccount[] = [];

    @state()
    private action!: WireAction;

    @state()
    private filterSheetOpen = false;

    @state()
    private loadingTemplates = true;

    @state()
    private templateSelection = false;

    @state()
    private validWireDateTime: ValidWireDateTime | undefined;

    @state()
    private isInternationalDisabled = false;

    @state()
    private createWireTemplateEntitlement = false;

    @state()
    private dialog = {
        content: '',
        title: '',
    };

    protected render() {
        return html` <tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'py-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <tm-section
                        class=${classMap({
                            'flex flex-col h-full': true,
                            'overflow-hidden': !this.isDesktop,
                            'mx-2 pb-2 rounded-lg': this.isDesktop,
                        })}
                    >
                        <div class="flex flex-col h-full">
                            ${this.isDesktop
                                ? html`<div
                                      class=${classMap({
                                          'flex justify-between items-center border-b': true,
                                          'bg-primary': !this.isDesktop,
                                      })}
                                  >
                                      <wires-container-tabs
                                          .activeTab=${`Templates`}
                                          @switchTab=${({ detail }: CustomEvent) => {
                                              switch (detail.activeTab) {
                                                  case 'OneTime':
                                                  case 'Recurring':
                                                  case 'WireFile':
                                                  case 'WireFileFormats':
                                                      this.navService.navigate('/wires', {
                                                          tab: detail.activeTab,
                                                      });
                                                      break;
                                                  case 'Creditors':
                                                      this.navService.navigate(`/wires/creditors`);
                                                      break;
                                                  default:
                                                      break;
                                              }
                                          }}
                                      ></wires-container-tabs>
                                      ${this.createWireTemplateEntitlement
                                          ? this.renderCreateButton()
                                          : nothing}
                                  </div>`
                                : nothing}
                            <div class="flex-grow">${this.renderTable()}</div>
                        </div>
                    </tm-section>
                </div>
                <wires-container-sidebar slot="sidebar-content"></wires-container-sidebar>
            </tm-sidebar>
            ${this.renderDetailSheet()} ${this.renderFiltersSheet()}`;
    }

    protected renderCreateButton() {
        return html`<tm-button
            importance="primary"
            .icon=${circlePlusIcon}
            class="mr-4"
            @click=${() => this.navService.navigate('/wires/template/create/new/usd')}
            >Create new template</tm-button
        >`;
    }

    protected async firstUpdated() {
        await this.tryFetch(
            async () =>
                Promise.all([
                    this.navService.getRouteData<WireTemplatesParameters>(),
                    this.wiresService.fetchTransferAccounts() as unknown as WireAccount[],
                    this.wiresService.checkValidDateTime(),
                    this.entitlementsService.isEnabled(Entitlements.CreateWireTemplate),
                ]),
            ([routeData, transferAccounts, validWireDateTime, createWireTemplateEntitlement]) => {
                const { action, backToPaymentsHub } = routeData.params;
                this.action = action?.toLowerCase() as WireAction;
                if (this.action === 'initiate') {
                    this.templateSelection = true;
                }
                if (backToPaymentsHub) {
                    this.templateSelection = false;
                }
                this.transferAccounts = transferAccounts;
                this.validWireDateTime = validWireDateTime;
                this.createWireTemplateEntitlement = createWireTemplateEntitlement;
                this.configureHeader();

                if (this.validWireDateTime.isAfterCutoff) {
                    this.isInternationalDisabled = true;
                    this.dialog.title = 'Cutoff Time Passed';
                    this.dialog.content =
                        'International wires may not be submitted after the processing cutoff time.';
                }

                if (this.validWireDateTime.isHoliday || this.validWireDateTime.isWeekend) {
                    this.isInternationalDisabled = true;
                    this.dialog.title = 'International Wires Unavailable';
                    this.dialog.content =
                        'International wires are not available on weekends or holidays.';
                }
            }
        );
    }

    protected async updated(changedProps: PropertyValues) {
        if (changedProps.has('wireTemplates')) {
            this.downloadService.updateConfig({
                filterModel: this.templateFilters,
                isRecurring: false,
                disabled: !this.wireTemplates.length,
            });
        }
    }

    private triggerDialog() {
        return this.dialogService.open(this.dialog.content, this.dialog.title, {
            buttons: [
                {
                    label: 'Ok',
                    reason: DynamicComponentExitReason.Confirm,
                },
            ],
            classes: ['max-w-[700px]'],
        });
    }

    private configureHeader() {
        const route = this.templateSelection ? '/wires/domestic/select/new/usd' : '/wires';
        this.headerService.configure({
            title: this.templateSelection ? 'Select Wire Template' : 'Wire Templates',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.WireTemplates}
                        .disabled=${true}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
            backAction: () => this.navService.navigate(route),
        });
    }

    private async filterTemplates(filters: WireTemplateQueryDto) {
        this.loadingTemplates = true;
        try {
            this.wireTemplates = await this.wiresService.searchTemplates(filters);
        } finally {
            this.loadingTemplates = false;
        }
    }

    private renderTable() {
        return html` <wire-templates-table
            class="h-full"
            .action=${this.action}
            .loading=${this.loadingTemplates}
            .wireTemplates=${this.wireTemplates}
            @select-template=${({ detail }: CustomEvent) => {
                const wireTemplate = detail.value as WireTemplate;
                if (wireTemplate.isInternational && this.isInternationalDisabled) {
                    this.triggerDialog();
                } else {
                    if (this.action === 'initiate') {
                        this.navService.navigate(
                            `/wires/template/initiate/${wireTemplate.id}/usd`,
                            {
                                debitAccounts: this.transferAccounts,
                            }
                        );
                    } else {
                        this.navService.navigate(`/wires/template/${wireTemplate.id}`);
                    }
                }
            }}
            @filter-wire-templates=${() => {
                this.filterSheetOpen = true;
            }}
            @delete-template=${({ detail }: CustomEvent) => {
                this.promptForDeletion(detail);
            }}
        ></wire-templates-table>`;
    }

    private renderDetailSheet() {
        return html`<wire-template-detail-sheet
            .open=${this.templateDetailsSheetOpen}
            .wireTemplate=${this.selectedWireTemplate}
            @close=${() => {
                this.templateDetailsSheetOpen = false;
            }}
        >
        </wire-template-detail-sheet>`;
    }

    private renderFiltersSheet() {
        return html`<wire-templates-filters-sheet
            .open=${this.filterSheetOpen}
            .templateSelection=${this.templateSelection}
            @close=${() => {
                this.filterSheetOpen = false;
            }}
            @applyFilters=${({ detail }: CustomEvent) => {
                this.templateFilters = detail as WireTemplateQueryDto;
                this.filterTemplates(this.templateFilters);
            }}
        ></wire-templates-filters-sheet>`;
    }

    private async deleteTemplate(id: number) {
        await this.tryFetch(
            () => this.wiresService.wiresDeleteTemplate(id),
            () => {
                if (this.templateFilters) {
                    this.filterTemplates(this.templateFilters);
                    this.requestUpdate();
                }
                this.notificationService.renderSuccess('Template deleted successfully');
            }
        );
    }

    private async promptForDeletion(id: number) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to delete this template?`,
            'Delete Wire Template',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Delete',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.deleteTemplate(id);
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplatesContainer;
    }
}
